import React, { useEffect, useRef } from 'react';
import s from './NewValues.module.css';

/* ---- Inline SVG Icons ---- */
const CompassIcon = () => (
  <svg className={s.icon} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="24" cy="24" r="20" />
    <polygon points="24,8 28,22 24,28 20,22" fill="currentColor" opacity="0.15" stroke="none" />
    <line x1="24" y1="4" x2="24" y2="10" />
    <line x1="24" y1="38" x2="24" y2="44" />
    <line x1="4" y1="24" x2="10" y2="24" />
    <line x1="38" y1="24" x2="44" y2="24" />
    <polygon points="24,10 28,22 24,26 20,22" />
    <polygon points="24,38 20,26 24,22 28,26" />
  </svg>
);

const EyeIcon = () => (
  <svg className={s.icon} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 24s7-14 20-14 20 14 20 14-7 14-20 14S4 24 4 24z" />
    <circle cx="24" cy="24" r="7" />
    <circle cx="24" cy="24" r="2.5" fill="currentColor" opacity="0.2" stroke="none" />
  </svg>
);

const HeartIcon = () => (
  <svg className={s.icon} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M24 42s-16-9.6-16-21a9.2 9.2 0 0 1 16-6.3A9.2 9.2 0 0 1 40 21c0 11.4-16 21-16 21z" />
    <path d="M24 42s-16-9.6-16-21a9.2 9.2 0 0 1 16-6.3A9.2 9.2 0 0 1 40 21c0 11.4-16 21-16 21z" fill="currentColor" opacity="0.08" stroke="none" />
  </svg>
);

const PuzzleIcon = () => (
  <svg className={s.icon} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 8h8v4a4 4 0 0 0 8 0V8h4v12h-4a4 4 0 0 0 0 8h4v12H28v-4a4 4 0 0 0-8 0v4H8V28h4a4 4 0 0 0 0-8H8V8h12z" />
    <path d="M20 8h8v4a4 4 0 0 0 8 0V8h4v12h-4a4 4 0 0 0 0 8h4v12H28v-4a4 4 0 0 0-8 0v4H8V28h4a4 4 0 0 0 0-8H8V8h12z" fill="currentColor" opacity="0.06" stroke="none" />
  </svg>
);

const VALUES = [
  {
    Icon: CompassIcon,
    title: 'תכנון שחושב קדימה',
    desc: 'כל החלטה נבחנת דרך עדשת העתיד - מה יחסוך כסף, מה ימנע טעויות, ומה ישרת אתכם לעשרות שנים.',
  },
  {
    Icon: EyeIcon,
    title: 'שקיפות מלאה',
    desc: 'אתם יודעים בדיוק מה קורה, מה עולה, ומתי. בלי הפתעות, בלי עלויות נסתרות.',
  },
  {
    Icon: HeartIcon,
    title: 'קשב אישי',
    desc: 'מספר מצומצם של פרויקטים במקביל - כי לכל בית מגיע יחס מלא ותשומת לב לפרטים.',
  },
  {
    Icon: PuzzleIcon,
    title: 'יפה וגם עובד',
    desc: 'לא מספיק שהבית ייראה טוב. הוא צריך לשרת אתכם - לנוחות, לפרקטיקה, לחיים שלכם.',
  },
];

export default function NewValues() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(s.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const cards = cardsRef.current.filter(Boolean);
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section className={s.section} ref={sectionRef} id="values">
      <div className={s.grain} aria-hidden="true" />
      <div className={s.glowA} aria-hidden="true" />
      <div className={s.glowB} aria-hidden="true" />

      <header className={s.header}>
        <span className={s.eyebrow}>&mdash; הערכים שלנו &mdash;</span>
        <h2 className={s.title}>מה מנחה אותנו בכל פרויקט</h2>
      </header>

      <div className={s.grid}>
        {VALUES.map((val, i) => (
          <article
            key={i}
            className={s.card}
            ref={(el) => { cardsRef.current[i] = el; }}
            style={{ '--stagger': `${i * 0.12}s` }}
          >
            <div className={s.iconWrap}>
              <val.Icon />
            </div>
            <h3 className={s.cardTitle}>{val.title}</h3>
            <p className={s.cardDesc}>{val.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
